import { categoryIcons as emojiCategoryIcons, buildPickerHtml as __buildPickerHtml } from './bp/emoji-templates.js';
import { styles as emojiStyles } from './bp/emoji-style.js';
import { emojiPickerLoaderBody } from './bp/templates.js';

const FgEmojiPicker = function (options) {

    this.options = options;
    this.emojiJson = null;

    if (!this.options) {
        return console.error('You must provide object as a first argument');
    }

    this.init = () => {

        // Generate style
        this.functions.generateStyle.call(this);

        this.selectors.trigger = this.options.hasOwnProperty('trigger') ? this.options.trigger : console.error('You must provide a trigger element like this - \'EmojiPicker.init({trigger: "selector"})\'');
        this.selectors.search = '.fg-emoji-picker-search input';
        this.selectors.emojiContainer = '.fg-emoji-picker-grid'
        this.emojiItems = undefined;
        this.variable.emit = this.options.emit || null;
        this.variable.removeOnSelection = this.options.removeOnSelection || false;
        this.variable.closeButton = this.options.closeButton || true;
        this.variable.position = this.options.position || null;
        this.variable.dir = this.options.dir || '';
        this.insertInto = this.options.insertInto || undefined;
        if (!this.selectors.trigger) { return; }

        this._bindEvents();
        this.options && this.options.preFetch && this.functions.fetchEmojiData();
    },

    this.variable = {
        position: null,
        dir: '',
        categoryIcons: emojiCategoryIcons
    }

    this.selectors = {
        emit: null,
        trigger: null,
        emojiPicker: '.fg-emoji-picker',
        emojiFooter: '.fg-emoji-picker-footer',
        emojiBody: '.fg-emoji-picker-all-categories',
        emojiHeader: '.fg-emoji-picker-categories',
        closeButton: '.fg-emoji-picker-close-button'
    }

    this.html = {
        pickerBody: () => {
            return emojiPickerLoaderBody;
        }
    }

    this.destroy = () => {
        document.querySelectorAll(this.selectors.emojiPicker).forEach(p => p.remove())
        this.emojiItems = undefined;
        return true;
    }

    this.setCaretPosition = (field, position) => {
        if (!field) {
            return;
        }
        if (field.setSelectionRange) {
            field.focus();
            field.setSelectionRange(position, position);
        } else if (field.createTextRange) { // For older IE
            const range = field.createTextRange();
            range.collapse(true);
            range.moveEnd('character', position);
            range.moveStart('character', position);
            range.select();
        }
    }

    this._handleBodyClick = (e) => {
        const target = e.target;

        // Open selector
        if (target.closest(this.selectors.trigger)) {
            this.functions.openEmojiSelector.call(this, e);
            return;
        }

        // Emit emoji
        if (target.closest('.fg-emoji-picker-item')) {
            this.functions.emitEmoji.call(this, e);
            return;
        }

        // Close button
        if (target.closest(this.selectors.closeButton)) {
            this.destroy();
            return;
        }

        // Remove picker if click is outside
        this.functions.removeEmojiPicker.call(this, e);
    }

    this._handlePickerClick = (e) => {
        const picker = e.currentTarget;
        const target = e.target;

        const scrollTo = (element, to, duration = 100) => {
            if (duration <= 0) return;
            const difference = to - element.scrollTop;
            const perTick = difference / duration * 10;

            setTimeout(() => {
                element.scrollTop += perTick;
                if (element.scrollTop === to) return;
                scrollTo(element, to, duration - 10);
            }, 10);
        };

        const filterLink = target.closest('.fg-emoji-picker-categories li[data-index]');
        if (filterLink) {
            picker.querySelector('.fg-emoji-picker-categories li.active')?.classList.remove('active');
            filterLink.classList.add('active');
            const listIndex = filterLink.dataset.index;
            const emojiBody = picker.querySelector(this.selectors.emojiBody);
            const targetCategory = emojiBody.querySelector(`ul#${listIndex}`);
            const headerHeight = picker.querySelector(this.selectors.emojiHeader).offsetHeight;
            scrollTo(emojiBody, targetCategory.offsetTop - headerHeight, 100);
        }
    };

    this._bindEvents = () => {
        document.body.addEventListener('click', this._handleBodyClick.bind(this));

        document.body.addEventListener('input', (e) => {
            if (e.target.matches(this.selectors.search)) {
                this.functions.search.call(this, e);
            }
        });

        window.addEventListener('keydown', e => {
            if (e.key === 'Escape' || e.keyCode === 27) {
                const picker = document.querySelector(this.selectors.emojiPicker);
                if (picker) {
                    picker.remove();
                    this.emojiItems = undefined;
                }
            }
        });
    }

    this._handlePickerScroll = (e) => {
        const picker = e.currentTarget;
        const emojiBody = e.target;
        const headerHeight = picker.querySelector(this.selectors.emojiHeader).offsetHeight;
        const scrollTop = emojiBody.scrollTop;

        let activeCategory = null;

        const categories = picker.querySelectorAll('ul.fg-emoji-picker-category');
        for (let i = categories.length - 1; i >= 0; i--) {
            const category = categories[i];
            if (category.offsetTop - headerHeight <= scrollTop) {
                activeCategory = category.id;
                break;
            }
        }

        if (activeCategory) {
            const currentActive = picker.querySelector('.fg-emoji-picker-categories li.active');
            if (currentActive && currentActive.dataset.index !== activeCategory) {
                currentActive.classList.remove('active');
                picker.querySelector(`.fg-emoji-picker-categories li[data-index="${activeCategory}"]`)?.classList.add('active');
            }
        }
    };

    this._buildPickerHtml = (emojiData) => {
        const pickerTemplate = `
        <div class="fg-emoji-picker">
            <div class="fg-emoji-picker-categories">%categories%
                <div class="fg-emoji-picker-search">
                    <input placeholder="Search emoji" autofocus />
                    ${this.variable.categoryIcons.search}
                </div>
            </div>
            <div>%pickerContainer%</div>
        </div>`;

        const closeBtn = this.variable.closeButton ? `<li><a class="fg-emoji-picker-close-button" href="#">${this.variable.categoryIcons.close}</a></li>` : '';
        let categoriesList = '';
        let emojiLists = '';
        let index = 0;

        for (const key in emojiData) {
            if (Object.hasOwnProperty.call(emojiData, key)) {
                index++;
                const keyToId = key.split(' ').join('-').split('&').join('').toLowerCase();
                const emojis = emojiData[key];

                categoriesList += `<li class="${index === 1 ? 'active' : ''}" id="${keyToId}" data-index="${keyToId}"><a href="#${keyToId}">${this.variable.categoryIcons[keyToId] || ''}</a></li>`;

                emojiLists += `
                <ul class="fg-emoji-picker-category ${index === 1 ? 'active' : ''}" id="${keyToId}" category-name="${key}">
                    <div class="fg-emoji-picker-container-title">${key}</div>
                    <div class="fg-emoji-picker-grid">`;

                emojis.forEach(item => {
                    emojiLists += `<li data-name="${item.description.toLowerCase()}"><a class="fg-emoji-picker-item" title="${item.description}" data-name="${item.description.toLowerCase()}" data-code="${item.code}" href="${item.emoji}">${item.emoji}</a></li>`;
                });

                emojiLists += `
                    </div>
                </ul>`;
            }
        }

        const categoriesHtml = `<ul>${categoriesList}${closeBtn}</ul>`;
        const allSmilesHtml = `<div class="fg-emoji-picker-all-categories">${emojiLists}</div>`;

        let pickerHtml = pickerTemplate
            .replace('%pickerContainer%', allSmilesHtml)
            .replace('%categories%', categoriesHtml);

        return pickerHtml;
    }

    this.functions = {

        // Put in place 
        putInPlace(myField, myValue) {
			// Handle contenteditable divs.
			if (myField.isContentEditable) {
				myField.focus();
				let sel, range;
				if (window.getSelection) {
					sel = window.getSelection();
					if (sel.getRangeAt && sel.rangeCount) {
						range = sel.getRangeAt(0);
						range.deleteContents();

						const el = document.createElement("div");
						el.innerHTML = myValue;
						const frag = document.createDocumentFragment();
						let node, lastNode;
						while ((node = el.firstChild)) {
							lastNode = frag.appendChild(node);
						}
						range.insertNode(frag);

						// Preserve the selection
						if (lastNode) {
							range = range.cloneRange();
							range.setStartAfter(lastNode);
							range.collapse(true);
							sel.removeAllRanges();
							sel.addRange(range);
						}
					}
				} else if (document.selection && document.selection.type !== "Control") {
					document.selection.createRange().pasteHTML(myValue);
				}
			} else { // Fallback for regular input/textarea
				myField.value += myValue;
				myField.focus();
			}
        },

        // Search
        search(e) {
            const val = e.target.value;
            if (!Array.isArray(this.emojiItems)) {
                this.emojiItems = Array.from(e.target.closest(this.selectors.emojiPicker).querySelectorAll(`${this.selectors.emojiBody} li`));
            }
            this.emojiItems.filter(emoji => {
                if (!emoji.getAttribute('data-name').match(val)) {
                    emoji.style.display = 'none'
                } else {
                    emoji.style.display = ''
                }
            })

            if (!val.length) this.emojiItems = undefined;
        },


        fetchEmojiData: () => {
            fetch(`${this.variable.dir}full-emoji-list.json`)
                .then(response => response.json())
                .then(object => { this.emojiJson = object });
        },

        generateStyle() {
            // Check if styles already injected
            if (!document.querySelector('#fg-emoji-picker-styles')) {
                document.head.insertAdjacentHTML('beforeend', `<style id="fg-emoji-picker-styles">${emojiStyles}</style>`);
            }
        },

        removeEmojiPicker(e) {
            const el = e.target;
            const picker = document.querySelector(this.selectors.emojiPicker);

            if (!el.closest(this.selectors.emojiPicker)) picker ? picker.remove() : false;
            this.emojiItems = undefined
        },


        emitEmoji(e) {

            const el = e.target.closest('.fg-emoji-picker-item');

            if (el.tagName.toLowerCase() == 'a' && el.classList.contains('fg-emoji-picker-item')) {
                e.preventDefault();

                let emoji = {
                    emoji: el.getAttribute('href'),
                    code: el.getAttribute('data-code')
                }
                if (this.variable.emit) this.variable.emit(emoji, this.triggerer)
                
                // If insert into option exists
                if (this.insertInto) {
                    this.functions.putInPlace(this.insertInto, emoji.emoji);
                    // After inserting, trigger the input event to enable the post button.
                    const inputEvent = new Event('input', { bubbles: true });
                    this.insertInto.dispatchEvent(inputEvent);
                }
                if (this.variable.removeOnSelection) {
                    const picker = document.querySelector(this.selectors.emojiPicker);
                    picker.remove();
                }
            }

        },


        // Open omoji picker
        openEmojiSelector(e) {

            let el = e.target.closest(this.selectors.trigger)
            if (el) {
                e.preventDefault();

                if (typeof this.variable.emit === 'function') this.triggerer = el

                // Insert picker
                const emojiPickerMain = new DOMParser().parseFromString(this.html.pickerBody.apply(this), 'text/html').body.firstElementChild;
                document.body.insertAdjacentElement('afterbegin', emojiPickerMain);

                let positions = {
                    buttonTop: window.scrollY + el.getBoundingClientRect().top,
                    buttonWidth: el.offsetWidth,
                    buttonFromLeft: el.getBoundingClientRect().left,
                    bodyHeight: document.body.offsetHeight,
                    bodyWidth: document.body.offsetWidth,
                    windowScrollPosition: window.pageYOffset,
                    emojiHeight: emojiPickerMain.offsetHeight,
                    emojiWidth: emojiPickerMain.offsetWidth,
                }


                // Element position object
                let position = {
                    top: positions.buttonTop - positions.emojiHeight,
                    left: positions.buttonFromLeft,
                    bottom: positions.buttonTop + el.offsetHeight,
                    right: positions.buttonFromLeft + positions.buttonWidth - positions.emojiWidth
                }


                // Positioning emoji container top
                if (this.variable.position) {
                    this.variable.position.forEach(elemPos => {

                        if (elemPos === 'right') {
                            emojiPickerMain.style.left = position[elemPos] + 'px';
                        } else if (elemPos === 'bottom' || elemPos === 'top' || elemPos === 'left') {
                            emojiPickerMain.style.top = position[elemPos] + 'px';
                        }  else {
                            emojiPickerMain.style[elemPos] = position[elemPos] + 'px';
                        }
                    })
                }



                // Emoji Picker Promise
                this.emojiPicker().then(emojiPicker => {

                    // Create node from 
                    const node = new DOMParser().parseFromString(emojiPicker, 'text/html').body.firstElementChild;
                    
                    emojiPickerMain.innerHTML = node.innerHTML;

                    emojiPickerMain.querySelector('input').focus();

                    // Add event listener on click
                    emojiPickerMain.addEventListener('click', (e) => {
                        if (e.target.closest('.fg-emoji-picker-categories')) {
                            this._handlePickerClick.call(this, e);
                        }
                    });                  

                    let scrollTimeout;
                    const emojiBody = emojiPickerMain.querySelector(this.selectors.emojiBody);
                    emojiBody.addEventListener('scroll', (e) => {
                        clearTimeout(scrollTimeout);
                        scrollTimeout = setTimeout(() => this._handlePickerScroll.call(this, e), 50);
                    });
                })
            }
        }
    },



    // Create emoji container / Builder engine
    this.emojiPicker = () => {
        if (this.emojiJson) {
            return Promise.resolve(this._buildPickerHtml(this.emojiJson));
        }

        return fetch(`${this.variable.dir}full-emoji-list.json`)
            .then(response => response.json())
            .then(emojiData => {
                this.emojiJson = emojiData;
                return this._buildPickerHtml(emojiData);
            });
    }

    this.init();

}

// Global compatibility
export { FgEmojiPicker };